import { useEffect, useState, useRef, lazy, Suspense } from "react";
import { useTranslation } from "react-i18next";
import { useStore } from "@/lib/store";
import { useAuth } from "@/hooks/useAuth";
import { useAnalytics } from "@/hooks/useAnalytics";
import { useLocation } from "wouter";
import Sidebar from "@/components/sidebar";
import ChatInterface from "@/components/chat-interface";
import { AnimatedWelcomeChat } from "@/components/animated-welcome-chat";

import WorksheetModal from "@/components/worksheet-modal";
import { OfferSheetModal } from "@/components/OfferSheetModal";
import { UserCloneModal } from "@/components/UserCloneModal";
import CampaignBuilder from "@/components/campaign-builder";
import OnboardingWizard from "@/pages/onboarding-wizard";
// Ads Wizard (VP-188) jest ukryty na produkcji i gate'owany rolą — lazy, żeby nie
// obciążać bundla najgorętszej trasy kodem, którego większość userów nie zobaczy.
const AdsWizardHub = lazy(() => import("@/pages/ads-wizard"));
const GoogleAdsWizard = lazy(() => import("@/pages/google-ads-wizard"));
const TikTokAdsWizard = lazy(() => import("@/pages/tiktok-ads-wizard"));
import { ErrorBoundary } from "@/components/error-boundary";
import AllegroAdsBuilder from "@/components/allegro-ads-builder";
import { MonitoringContent } from "@/pages/monitoring";
import DashboardPage from "@/pages/dashboard";
import ContentCreator from "@/pages/content-creator";
import ViralBoosterChat from "@/components/viralbooster-chat";
import ManagerDashboardPage from "@/pages/manager-dashboard";
import type { OfferSheet } from "@shared/schema";
import { useQuery } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { Menu, X, ChevronLeft, BarChart3 } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useTheme } from "@/components/theme-provider";
import { standardAllowedModules } from "@shared/module-access";

export default function ChatPage() {
  const { t } = useTranslation();
  const { resolvedTheme } = useTheme();
  const {
    modules,
    currentModule,
    setCurrentModule,
    setCurrentChatId,
    worksheetModalOpen,
    offerSheetModalOpen,
    userCloneModalOpen,
  } = useStore();

  const { user } = useAuth();
  const { trackModuleUsage } = useAnalytics();
  const [selectedOfferSheet, setSelectedOfferSheet] = useState<OfferSheet | null>(null);
  const [sidebarOpen, setSidebarOpen] = useState(true); // Default to open on desktop
  const [location, setLocation] = useLocation();

  // VP-116: rozwiązany serwerowo dostęp do Performance (dziedziczenie po agencji).
  const hasPerformance = (user as { hasPerformance?: boolean } | null)?.hasPerformance !== false;

  // Redirect dashboard-only users to /dashboard (opt-in modules don't count — see shared/module-access.ts)
  const allowedModules = standardAllowedModules(user?.allowedModules ?? null);
  const isDashboardOnly =
    allowedModules !== null && allowedModules.length === 1 && allowedModules[0] === "dashboard";

  useEffect(() => {
    if (isDashboardOnly && location !== "/dashboard") {
      setLocation("/dashboard");
    }
  }, [isDashboardOnly, location, setLocation]);

  // Idiotoodporny onboarding: user na trialu (bez płatnej subskrypcji) ląduje
  // domyślnie w szybkim torze kreatora kampanii, dopóki nie wyjdzie z wizarda
  // (flaga vpOnboardingExited w localStorage ustawiana przyciskiem „Wyjdź z kreatora").
  const trialUser = user as {
    subscriptionActive?: boolean;
    trialEndsAt?: string | Date | null;
  } | null;
  const isTrialUser =
    !!trialUser &&
    trialUser.subscriptionActive !== true &&
    !!trialUser.trialEndsAt &&
    new Date(trialUser.trialEndsAt).getTime() > Date.now();

  // Distraction-free wizard: trial-user w kreatorze (do czasu wyjścia) widzi tylko
  // kroki onboardingu, bez sidebara/banerów/checklisty (pełnoekranowy overlay).
  const isOnboardingWizard =
    isTrialUser &&
    currentModule === "campaign-creator" &&
    typeof window !== "undefined" &&
    !localStorage.getItem("vpOnboardingExited");

  const { data: onboardingMetaStatus } = useQuery<{ connected?: boolean }>({
    queryKey: ["meta-status-campaign"],
    queryFn: () => apiRequest("GET", "/api/integrations/meta/status"),
    enabled: isOnboardingWizard,
  });
  const onboardingStep = onboardingMetaStatus?.connected ? 2 : 1;

  const exitOnboardingWizard = () => {
    try {
      localStorage.setItem("vpOnboardingExited", "1");
    } catch {
      /* storage off */
    }
    setLocation("/chat/home");
  };

  useEffect(() => {
    if (
      isTrialUser &&
      !isDashboardOnly &&
      currentModule === "home" &&
      typeof window !== "undefined" &&
      !localStorage.getItem("vpOnboardingExited")
    ) {
      setLocation("/chat/campaign-creator");
    }
  }, [isTrialUser, isDashboardOnly, currentModule, setLocation]);

  // Handle URL parameters for module selection
  const prevModuleRef = useRef<string | null>(null);
  useEffect(() => {
    // VP-105: /viralbooster is the ORGANIC agent home (renders ViralBoosterChat via organic-home).
    if (location === "/viralbooster" || location.startsWith("/viralbooster")) {
      setCurrentModule("organic-home");
      return;
    }
    // Sync module from URL → state (one-way: only on location change)
    const pathMatch = location.match(/^\/chat\/([^/?]+)/);
    const pathModule = pathMatch?.[1];
    if (pathModule) {
      if (prevModuleRef.current && pathModule !== prevModuleRef.current) {
        setCurrentChatId(null);
      }
      prevModuleRef.current = pathModule;
      setCurrentModule(pathModule);
      return;
    }

    // Fallback: query param ?module=xxx
    const urlParams = new URLSearchParams(location.split("?")[1] || "");
    const moduleParam = urlParams.get("module");

    if (moduleParam) {
      if (prevModuleRef.current && moduleParam !== prevModuleRef.current) {
        setCurrentChatId(null);
      }
      prevModuleRef.current = moduleParam;
      setCurrentModule(moduleParam);
    }
  }, [location, setCurrentModule, setCurrentChatId]);

  // Track module usage time
  useEffect(() => {
    const startTime = Date.now();

    return () => {
      const endTime = Date.now();
      const timeSpent = Math.round((endTime - startTime) / 1000); // seconds
      if (timeSpent > 5) {
        // Only track if spent more than 5 seconds
        trackModuleUsage(currentModule, timeSpent);
      }
    };
  }, [currentModule, trackModuleUsage]);

  // Moduły ukryte z sidebara — przekieruj do offer-builder
  const hiddenModules = new Set(["clone-yourself", "email-creator"]);
  // Moduły wchłonięte przez Content Creator — renderowane wewnątrz niego
  const contentSubModules = new Set(["reel-creator", "youtube-mode", "carousel-creator"]);
  const resolvedModule = hiddenModules.has(currentModule)
    ? "offer-builder"
    : contentSubModules.has(currentModule)
      ? "content-creator"
      : currentModule;
  // VP-116: to jest REALNA brama do dashboardu — sidebar prowadzi na /chat/dashboard,
  // nie na /dashboard, więc gating tras w App.tsx sam z siebie tego nie łapał.
  // Te trzy moduły nie mają wymagań `paid_ads.*` w MODULE_PERMISSION_REQUIREMENTS
  // (Organic zachowuje `dashboard.view`/`monitoring.view`), więc filtr uprawnień
  // ich nie odsiewa — blokada musi iść po planie.
  const performanceModules = new Set(["dashboard", "moiklienci", "monitoring"]);
  const blockedByPlan = !hasPerformance && performanceModules.has(resolvedModule);
  const effectiveModule = blockedByPlan ? "organic-home" : resolvedModule;

  // Poprawiamy też adres, żeby user nie został na /chat/dashboard z podmienioną treścią.
  // MUSI stać przed early-returnem niżej (rules-of-hooks).
  useEffect(() => {
    if (blockedByPlan) setLocation("/viralbooster");
  }, [blockedByPlan, setLocation]);

  // Early-return PO wszystkich hookach (rules-of-hooks; review #7) — nie renderuj
  // nic gdy przekierowujemy dashboard-only usera.
  if (isDashboardOnly && location !== "/dashboard") {
    return (
      <div className="h-screen bg-[#030407] flex items-center justify-center">
        <div className="text-white/40 text-sm">{t("chat.redirecting")}</div>
      </div>
    );
  }

  const handleOfferSheetSelect = (offerSheet: OfferSheet) => {
    setSelectedOfferSheet(offerSheet);
  };

  const isLightTheme = resolvedTheme === "light";

  // ── Distraction-free onboarding wizard (pełnoekranowy overlay) ──
  if (isOnboardingWizard) {
    const steps = [
      { n: 1, label: t("onboardingWizard.step1") },
      { n: 2, label: t("onboardingWizard.step2") },
      { n: 3, label: t("onboardingWizard.step3") },
    ];
    return (
      <div className="fixed inset-0 z-[60] bg-[#030407] flex flex-col">
        <header className="shrink-0 flex items-center justify-between gap-4 px-5 h-16 border-b border-white/[0.08]">
          <img
            src="https://pub-9c0a0846a5c24484849bf7e3f13e4239.r2.dev/viralpartners/logo-800w.webp"
            alt="Viral Partners"
            className="h-7 object-contain"
          />
          <nav className="flex items-center gap-2 sm:gap-3">
            {steps.map((s, i) => (
              <div key={s.n} className="flex items-center gap-2 sm:gap-3">
                <div className="flex items-center gap-2">
                  <span
                    className={`flex items-center justify-center w-6 h-6 rounded-full text-[11px] font-bold transition-colors ${
                      s.n < onboardingStep
                        ? "bg-green-500 text-white"
                        : s.n === onboardingStep
                          ? "bg-[#ef3e29] text-white"
                          : "bg-white/10 text-white/50"
                    }`}
                  >
                    {s.n}
                  </span>
                  <span
                    className={`text-xs font-medium hidden sm:inline ${s.n <= onboardingStep ? "text-white" : "text-white/40"}`}
                  >
                    {s.label}
                  </span>
                </div>
                {i < steps.length - 1 && <span className="w-4 sm:w-8 h-px bg-white/15" />}
              </div>
            ))}
          </nav>
          <button
            type="button"
            onClick={exitOnboardingWizard}
            className="text-white/45 hover:text-white/80 text-xs underline underline-offset-2 whitespace-nowrap"
          >
            {t("campaignBuilder.exitOnboarding")}
          </button>
        </header>
        <div className="flex-1 min-h-0 overflow-y-auto">
          <ErrorBoundary fallbackTitle={t("chat.campaignBuilderError")}>
            <CampaignBuilder />
          </ErrorBoundary>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`flex h-full relative overflow-hidden ${isLightTheme ? "bg-[#f8fafc]" : "bg-[#030407]"}`}
    >
      {/* Mobile Sidebar Overlay */}
      {!isDashboardOnly && (
        <AnimatePresence>
          {sidebarOpen && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black bg-opacity-50 z-40 md:hidden"
              onClick={() => setSidebarOpen(false)}
            />
          )}
        </AnimatePresence>
      )}

      {/* Sidebar Toggle Button - desktop only (mobile uses header hamburger) */}
      {!isDashboardOnly && (
        <motion.button
          onClick={() => setSidebarOpen(!sidebarOpen)}
          className={`hidden md:flex fixed top-4 left-4 z-50 p-3 backdrop-blur-xl rounded-xl transition-all duration-200 shadow-lg border btn-modern ${isLightTheme ? "bg-white/80 text-black border-black/10 hover:bg-white" : "bg-black/40 text-white border-white/[0.06] hover:bg-black/60"}`}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          data-testid="sidebar-toggle"
        >
          <AnimatePresence mode="wait">
            {sidebarOpen ? (
              <motion.div
                key="close"
                initial={{ rotate: -90, opacity: 0 }}
                animate={{ rotate: 0, opacity: 1 }}
                exit={{ rotate: 90, opacity: 0 }}
                transition={{ duration: 0.2 }}
              >
                <ChevronLeft size={20} />
              </motion.div>
            ) : (
              <motion.div
                key="open"
                initial={{ rotate: 90, opacity: 0 }}
                animate={{ rotate: 0, opacity: 1 }}
                exit={{ rotate: -90, opacity: 0 }}
                transition={{ duration: 0.2 }}
              >
                <Menu size={20} />
              </motion.div>
            )}
          </AnimatePresence>
        </motion.button>
      )}

      {/* Sidebar - Full Height with proper scrolling */}
      {!isDashboardOnly && (
        <AnimatePresence>
          {sidebarOpen && (
            <motion.div
              initial={{ x: -320, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              exit={{ x: -320, opacity: 0 }}
              transition={{
                type: "spring",
                stiffness: 300,
                damping: 30,
                opacity: { duration: 0.2 },
              }}
              className="fixed md:relative inset-y-0 left-0 z-40 w-72 lg:w-80 h-full flex flex-col overscroll-behavior-none"
            >
              <Sidebar />
              {/* Close button for mobile */}
              <button
                onClick={() => setSidebarOpen(false)}
                className="absolute top-4 right-4 md:hidden p-2 text-white hover:bg-white/10 rounded-lg transition-colors micro-bounce"
                data-testid="sidebar-close-mobile"
              >
                <X size={20} />
              </button>
            </motion.div>
          )}
        </AnimatePresence>
      )}

      {/* Main Content Area */}
      <main
        className={`flex-1 flex flex-col min-w-0 relative overflow-hidden h-full transition-[padding] duration-300 ${!sidebarOpen && !isDashboardOnly ? "md:pl-16" : ""}`}
      >
        {/* Mobile Header with Hamburger - Sticky */}
        <div
          className={`md:hidden sticky top-0 z-10 flex items-center justify-between p-4 border-b ${isLightTheme ? "bg-white/90 border-black/10" : "bg-[#0a0c10] border-white/[0.08]"}`}
        >
          {!isDashboardOnly ? (
            <button
              onClick={() => setSidebarOpen(true)}
              className={`p-2 rounded-lg transition-colors ${isLightTheme ? "text-black/70 hover:bg-black/5" : "text-white hover:bg-white/10"}`}
            >
              <Menu size={24} />
            </button>
          ) : (
            <div className="w-10" />
          )}

          <div className="flex items-center space-x-2">
            <img
              src="https://pub-9c0a0846a5c24484849bf7e3f13e4239.r2.dev/viralpartners/logo-800w.webp"
              alt="Viral Partners Logo"
              className="w-8 h-8 object-contain"
            />
            <div>
              <h1 className={`font-semibold text-sm ${isLightTheme ? "text-black" : "text-white"}`}>
                VIRAL PARTNERS
              </h1>
              <p className={`text-xs ${isLightTheme ? "text-black/60" : "text-white/60"}`}>
                MarketingOS
              </p>
            </div>
          </div>

          {/* Dashboard shortcut */}
          {!isDashboardOnly ? (
            <button
              onClick={() => {
                setCurrentModule("dashboard");
                setCurrentChatId(null);
                setLocation("/chat/dashboard");
              }}
              className={`p-2 rounded-lg transition-colors ${currentModule === "dashboard" ? "bg-[#ef3e29] text-white" : isLightTheme ? "text-black/60 hover:bg-black/5 hover:text-black" : "text-white/60 hover:bg-white/10 hover:text-white"}`}
            >
              <BarChart3 size={22} />
            </button>
          ) : (
            <div className="w-10" />
          )}
        </div>

        {/* Main Content Area - properly scrollable */}
        <div className="flex-1 flex flex-col overflow-hidden">
          {effectiveModule === "organic-home" ? (
            <ViralBoosterChat />
          ) : effectiveModule === "dashboard" ? (
            <DashboardPage embedded />
          ) : effectiveModule === "moiklienci" ? (
            <ManagerDashboardPage embedded />
          ) : effectiveModule === "monitoring" ? (
            <MonitoringContent />
          ) : effectiveModule === "ads-wizard" ? (
            <Suspense fallback={null}>
              <AdsWizardHub />
            </Suspense>
          ) : effectiveModule === "google-ads-wizard" ? (
            <Suspense fallback={null}>
              <GoogleAdsWizard />
            </Suspense>
          ) : effectiveModule === "tiktok-ads-wizard" ? (
            <Suspense fallback={null}>
              <TikTokAdsWizard />
            </Suspense>
          ) : effectiveModule === "onboarding-wizard" ? (
            <OnboardingWizard />
          ) : effectiveModule === "campaign-creator" ? (
            <ErrorBoundary fallbackTitle={t("chat.campaignBuilderError")}>
              <CampaignBuilder />
            </ErrorBoundary>
          ) : effectiveModule === "allegro-ads" ? (
            <AllegroAdsBuilder />
          ) : effectiveModule === "content-creator" ? (
            <ContentCreator />
          ) : effectiveModule === "home" ? (
            <AnimatedWelcomeChat
              username={user?.firstName || user?.username || user?.email || ""}
              onSubmit={(text) => {
                sessionStorage.setItem("pendingPrompt", text);
                setCurrentModule("offer-builder");
                setCurrentChatId(null);
                setLocation("/chat/offer-builder");
              }}
              onModuleSelect={(moduleId, prompt) => {
                if (prompt) sessionStorage.setItem("pendingPrompt", prompt);
                setCurrentModule(moduleId);
                setCurrentChatId(null);
                setLocation(`/chat/${moduleId}`);
              }}
              availableModuleIds={modules?.map((m) => m.id) || []}
            />
          ) : (
            <ChatInterface />
          )}
        </div>
      </main>

      {worksheetModalOpen && <WorksheetModal />}
      {offerSheetModalOpen && <OfferSheetModal />}
      {userCloneModalOpen && <UserCloneModal />}
    </div>
  );
}
